import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { graphql } from "react-relay"
import { useUser } from "@/apis/bff"
import { useCustomMutation } from "@/hooks/useCustomMutation"
import { toast } from "@/hooks/useToast"
import {
  RequestTakeOverPatientFileLockInput,
  useRequestTakeOverPatientMutation$data,
  useRequestTakeOverPatientMutation as useRequestTakeOverPatientMutationType,
} from "@/modules/patients/services/__generated__/useRequestTakeOverPatientMutation.graphql"
import useCallContextStore from "@/stores/useCallContextStore"

const requestTakeOverPatientMutation = graphql`
  mutation useRequestTakeOverPatientMutation($input: RequestTakeOverPatientFileLockInput!) {
    requestTakeOverPatientFileLock(input: $input) {
      errors {
        ... on Error {
          __typename
          message
        }
      }
      patientFileLockOutput {
        ...PatientFileLockOutputFragment
        patientFileLock {
          lockedBy
        }
        inContextOfAppointment {
          chatDetails {
            chatThreadId
          }
        }
      }
    }
  }
`

const useRequestTakeOverPatientMutation = () => {
  const [commit, isInFlight] = useCustomMutation<useRequestTakeOverPatientMutationType>(requestTakeOverPatientMutation)
  const { userName } = useUser()
  const { t } = useTranslation()

  return [
    useCallback(
      (
        input: RequestTakeOverPatientFileLockInput,
        onPatientFileLockGranted?: (response: useRequestTakeOverPatientMutation$data) => void,
        onPatientFileLockRequestPending?: (response: useRequestTakeOverPatientMutation$data) => void,
        onError?: (error: Error) => void,
      ) => {
        // Never rejects: call sites invoke this without catching, so a rejection would become an unhandled rejection.
        return new Promise<useRequestTakeOverPatientMutation$data>((resolveWithGrantedResponse) => {
          const callContext = useCallContextStore.getState()
          commit({
            variables: {
              input: {
                patientId: input.patientId,
                appointmentId: input.appointmentId,
                callId: callContext.callID,
                inboundCallId: callContext.inboundCallId,
              },
            },
            onMutationResultError(errors) {
              toast({ description: t("error.500.description") })
              onError?.(new Error(errors[0]?.message))
            },
            onCompleted(response) {
              const isPatientFileLockHeldByCurrentUser =
                response.requestTakeOverPatientFileLock.patientFileLockOutput?.patientFileLock?.lockedBy === userName

              if (isPatientFileLockHeldByCurrentUser) {
                onPatientFileLockGranted?.(response)
                resolveWithGrantedResponse(response)
              } else {
                onPatientFileLockRequestPending?.(response)
              }
            },
            onError(error) {
              toast({ description: t("error.500.description") })
              onError?.(error)
            },
          })
        })
      },
      [commit, t, userName],
    ),
    isInFlight,
  ] as const
}

export { useRequestTakeOverPatientMutation }
